import { useState } from "react";

import { BiChevronLeft, BiChevronRight } from "react-icons/bi";

import CircularLoading from "../CircularLoading";

import {
  PaginationBlock,
  PaginationButton,
  Table as StyledTable,
  TableContainer,
  PaginationInfo,
  PageNumberButton,
} from "./style";

const Table = ({
  columns,
  data,
  minWidth,
  isClickableRow,
  onClickRow,
  onChangePage,
  prevButtonDisabled,
  nextButtonDisabled,
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
}) => {
  const [loadingButton, setLoadingButton] = useState(null);

  const handleChangePage = async (pageType) => {
    setLoadingButton(pageType);
    await onChangePage?.(pageType);
    setLoadingButton(null);
  };

  const handlePageNumberClick = async (pageNumber) => {
    setLoadingButton(pageNumber);
    await onChangePage?.(`page-${pageNumber}`);
    setLoadingButton(null);
  };

  const renderPageNumbers = () => {
    if (!totalPages || totalPages <= 1) return null;

    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PageNumberButton
          key={i}
          active={i === currentPage}
          onClick={() => handlePageNumberClick(i)}
          disabled={loadingButton !== null}
        >
          {loadingButton === i ? <CircularLoading size="16px" /> : i}
        </PageNumberButton>
      );
    }

    return pages;
  };

  const startItem = data?.length ? (currentPage - 1) * (itemsPerPage || data.length) + 1 : 0;
  const endItem = startItem + (data?.length || 0) - 1;

  return (
    <>
      <TableContainer>
        <StyledTable minWidth={minWidth} isClickableRow={isClickableRow}>
          <colgroup>
            {columns.map(column => (
              <col key={column.key} style={{ width: column.width || 'auto' }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {columns.map(column => (
                <th key={column.key} style={{ width: column.width || 'auto' }}>{column.label}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            {data?.map((row, index) => (
              <tr key={index} onClick={() => onClickRow?.(row)}>
                {
                  columns.map(column => (
                    <td key={column.key}>
                      {column.render ? column.render(row ?? {}) : row[column.key]?.toString()}
                    </td>
                  ))
                }
              </tr>
            ))}
          </tbody>
        </StyledTable>
      </TableContainer>
      
      {(totalPages > 1 || totalItems) && (
        <>
          {totalItems && (
            <PaginationInfo>
              Showing {startItem}-{endItem} of {totalItems} orders
            </PaginationInfo>
          )}
          
          <PaginationBlock>
            <PaginationButton 
              disabled={prevButtonDisabled || currentPage === 1} 
              onClick={() => handleChangePage("prev")}
            >
              {loadingButton === "prev" ? <CircularLoading size="16px" /> : <BiChevronLeft />}
            </PaginationButton>

            {renderPageNumbers()}

            <PaginationButton 
              disabled={nextButtonDisabled || currentPage === totalPages} 
              onClick={() => handleChangePage("next")}
            >
              {loadingButton === "next" ? <CircularLoading size="16px" /> : <BiChevronRight />}
            </PaginationButton>
          </PaginationBlock>
        </>
      )}
    </>
  );
};

export default Table;
